import * as Yup from 'yup';

import { getMaxStringSchema, getStringSchema } from 'src/compass/shared/schemas';

// Direct paths, not the application barrel: this file is inside it, and importing it from
// itself is a cycle (see CasPendingEmailsPill in CasContextProviders).
import {
  MAX_SMS_RECIPIENTS,
  MAX_SMS_TEXT_LENGTH,
  RecipientOptionType,
  channelOf,
  resolveChannel,
} from './composeMode';
import { messageCategorySchema } from './messageCategory';
import { getMaxBodySchema } from './inlineImages';

/*
  What a compose window will not send, whoever is sending it.

  Shared by the SCE Reports window and the Customers one: the rules are about the MESSAGE - one
  channel per send, the carrier's caps, a body for whichever channel is in play - and none of
  them is about what the message is for.
*/
/*
  Subject and the two body fields only apply to one channel each, so each is
  required only when that channel is the one being sent on.
*/
const isEmailMode = (sendAs: any, to: any, cc: any) => resolveChannel(sendAs, to, cc) === 'email';

export const composeSchema = Yup.object().shape({
  category: messageCategorySchema,
  sendAs: Yup.string(),
  to: Yup.array()
    .required('Recipients is required')
    .min(1, 'Must have at least 1 Recipient')
    /*
      One message goes out on one channel, so a mix of email-only and phone-only
      recipients cannot all be reached - whichever does not match the resolved
      channel would be dropped without a word. Contacts reachable both ways are
      not part of the conflict; they follow whatever the channel settles on.

      On To rather than CC so the message is visible even when CC is hidden.
    */
    .test('single-channel-recipients', '', function validateOneChannel(value) {
      const { cc } = this.parent;
      const everyone: RecipientOptionType[] = [...(value ?? []), ...(cc ?? [])];

      const emailOnly = everyone.filter((option) => channelOf(option) === 'email');
      const phoneOnly = everyone.filter((option) => channelOf(option) === 'sms');

      if (!emailOnly.length || !phoneOnly.length) return true;

      return this.createError({
        message:
          'Recipients mix email and phone#, and a message is sent one way or the other. ' +
          `Remove either the email (${emailOnly.map((o) => o.name).join(', ')}) ` +
          `or the phone# (${phoneOnly.map((o) => o.name).join(', ')}) recipients.`,
      });
    })
    .test('sms-recipient-limit', '', function validateSmsRecipientCount(value) {
      const { sendAs, cc } = this.parent;

      if (isEmailMode(sendAs, value, cc)) return true;

      const total = (value?.length ?? 0) + (cc?.length ?? 0);

      if (total <= MAX_SMS_RECIPIENTS) return true;

      return this.createError({
        message:
          `A text can reach at most ${MAX_SMS_RECIPIENTS} recipients (To and CC together). ` +
          `Remove ${total - MAX_SMS_RECIPIENTS}.`,
      });
    }),
  cc: Yup.array().nullable(),
  subject: Yup.string().when(['sendAs', 'to', 'cc'], {
    is: isEmailMode,
    then: () => getStringSchema(255, 'Subject'),
    otherwise: (schema) => schema.notRequired(),
  }),
  content: Yup.string().when(['sendAs', 'to', 'cc'], {
    is: isEmailMode,
    // Counted as sent, a picture as its reference - see bodyLength.
    then: () => getMaxBodySchema().required('Email body is required.'),
    otherwise: (schema) => schema.notRequired(),
  }),
  textContent: Yup.string().when(['sendAs', 'to', 'cc'], {
    is: (sendAs: any, to: any, cc: any) => !isEmailMode(sendAs, to, cc),
    // The carrier's cap. Also re-checked after the spam-safe rewrite, which can lengthen the text:
    // onSubmit writes the rewrite back with shouldValidate, so an over-long result shows here.
    then: () =>
      getMaxStringSchema()
        .max(MAX_SMS_TEXT_LENGTH, `A text can be at most ${MAX_SMS_TEXT_LENGTH} characters.`)
        .required('Message body is required.'),
    otherwise: (schema) => schema.notRequired(),
  }),
});

